'use client';

import { useState, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuth } from './useAuth';
import { MicrosoftGraphService } from '../services/microsoftGraph';
import { User } from '@microsoft/microsoft-graph-types';

export function useMicrosoftGraph() {
  const { instance, accounts } = useMsal();
  const { provider } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (provider !== 'azure-ad') return null;

    const scopes = ["User.Read", "User.ReadBasic.All", "openid", "profile", "offline_access"];
    let account = (accounts && accounts[0]) || instance.getAllAccounts()[0];
    if (!account) {
      try {
        const ssoResult = await instance.ssoSilent({ scopes });
        account = instance.getAllAccounts()[0];
        if (!account && ssoResult.account) account = ssoResult.account || undefined;
      } catch {
        try {
          const loginResult = await instance.loginPopup({ scopes });
          account = loginResult.account || instance.getAllAccounts()[0];
        } catch (loginErr) {
          console.error('loginPopup failed; cannot obtain account', loginErr);
          return null;
        }
      }
    }

    if (!account) return null;

    try {
      const tokenResponse = await instance.acquireTokenSilent({ scopes, account });
      return tokenResponse.accessToken;
    } catch (silentErr: unknown) {
      const name = (silentErr as { name?: string })?.name || '';
      const interactionRequired = /InteractionRequired/i.test(name) || /interaction_required/i.test(String((silentErr as { errorCode?: string })?.errorCode));
      if (!interactionRequired) {
        try {
          const forced = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
          return forced.accessToken;
        } catch {
        }
      }
      try {
        const popupResult = await instance.acquireTokenPopup({ scopes });
        return popupResult.accessToken;
      } catch (popupErr) {
        console.error('acquireTokenPopup failed', popupErr);
        return null;
      }
    }
  }, [accounts, instance, provider]);

  const graphService = useMemo(() => {
    return new MicrosoftGraphService(getAccessToken);
  }, [getAccessToken]);

  const executeGraphCall = useCallback(async <T>(
    operation: () => Promise<T>
  ): Promise<T | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await operation();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Microsoft Graph API error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCurrentUser = useCallback(async (): Promise<User | null> => {
    return executeGraphCall(() => graphService.getCurrentUser());
  }, [executeGraphCall, graphService]);

  const getCurrentUserDetailed = useCallback(async (): Promise<User | null> => {
    return executeGraphCall(() => graphService.getCurrentUserDetailed());
  }, [executeGraphCall, graphService]);

  const getUserPhoto = useCallback(async (size: string = '96x96'): Promise<string | null> => {
    return executeGraphCall(() => graphService.getUserPhoto(size));
  }, [executeGraphCall, graphService]);

  const getUserPhotoMetadata = useCallback(async () => {
    return executeGraphCall(() => graphService.getUserPhotoMetadata());
  }, [executeGraphCall, graphService]);

  return {
    graphService,
    loading,
    error,
    getCurrentUser,
    getCurrentUserDetailed,
    getUserPhoto,
    getUserPhotoMetadata,
    getDisplayName: MicrosoftGraphService.getDisplayName,
    getEmail: MicrosoftGraphService.getEmail,
    getFullName: MicrosoftGraphService.getFullName,
    clearError: () => setError(null),
  };
}
